import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { useOrder } from '../../context/OrderContext';
import { api } from '../../services/api';
import {
    SupplyTypeSelector,
    BarConfigSelector
} from '../../selectors';
import DrainageProcess from './DrainageProcess';

// Profiles limited to max 3000mm for misura
const PROFILES_MAX_3M = ['176', '186', '6S'];

const ProcessingSection = forwardRef(({ onComplete, onBack }, ref) => {
    const { state, dispatch } = useOrder();
    const currentProfileType = state.orderDetails?.profileType || '';
    const isMax3m = PROFILES_MAX_3M.includes(currentProfileType);
    const maxMisuraMm = isMax3m ? 3000 : 6000;
    const [step, setStep] = useState(1);
    const [detailModal, setDetailModal] = useState({ open: false, field: '', label: '' });
    const [processingData, setProcessingData] = useState({
        supplyType: null,
        barLength: null,
        barGroups: null,
        quantity: null,
        customBars: [],
        needsDrainage: null,
        drainageType: null,
        stepType: null,
        customHoles: null,
        photos: [],
        notes: '',
        metriIndicativi: '',
        vuoiGiunzioni: false,
        giunzioniDettaglio: '',
        vuoiCorrimano: false,
        corrimanoDettaglio: '',
        vuoiTappi: false,
        vuoiSpessori: false,
        vuoiCoprifilo: false,
        coprifiloDettaglio: '',
        vuoiLed: false,
        capsQuantities: {},
        spacersQuantity: 0
    });

    const updateProcessingData = (data) => {
        setProcessingData(prev => ({
            ...prev,
            ...data
        }));
    };

    // Handle back navigation within ProcessingSection
    // Returns true if handled internally, false if parent should handle
    const handleInternalBack = () => {
        // Define the back navigation logic based on current step and supply type
        switch (step) {
            case 1:
                // At step 1, signal to parent that we can't go back further
                // Return false so parent knows to handle navigation to previous step
                return false;
            case 2:
                // From bar config, go back to supply type selection
                setStep(1);
                break;
            case 4:
                // From drainage, go back to bar config (for barre flow)
                setStep(2);
                break;
            case 6:
                // From misura bars input, go back to supply type selection
                setStep(1);
                break;
            case 8:
                // From misura drainage question, go back to bars input
                setStep(6);
                break;
            case 9:
                // From photo upload (progettazione), go back based on flow
                if (processingData.supplyType === 'progettazione') {
                    setStep(1); // Direct progettazione flow
                } else {
                    setStep(8); // From misura with drainage
                }
                break;
            case 10:
                // From notes, go back to photo upload
                setStep(9);
                break;
            case 12:
                // From caps config, go back to drainage
                setStep(4);
                break;
            case 13:
                // From spacers config, go back to caps
                setStep(12);
                break;
            default:
                // Default: go back to step 1
                setStep(1);
                break;
        }
    };

    // Expose handleInternalBack to parent via ref
    useImperativeHandle(ref, () => ({
        goBack: handleInternalBack
    }));

    // Handle supply type selection
    const handleSupplyTypeSelect = (supplyType) => {
        updateProcessingData({ supplyType });

        if (supplyType === 'barre') {
            setStep(2); // Go to bar configuration
        } else if (supplyType === 'misura') {
            setStep(6); // Go to number of bars input
        } else if (supplyType === 'progettazione') {
            setStep(9); // Go to photo upload
        }
    };

    // Handle bar configuration for "Fornitura a Barre"
    const handleBarConfigSelect = (config) => {
        const groups = config.barGroups || [{ barLength: config.barLength, quantity: config.quantity }];
        const totalMeters = groups.reduce((sum, g) => sum + g.quantity * g.barLength, 0);

        updateProcessingData({
            barLength: config.barLength,
            quantity: config.quantity,
            barGroups: groups
        });

        // Update the global order context
        dispatch({
            type: 'UPDATE_ORDER',
            payload: {
                quantity: totalMeters,
                length: config.barLength * 1000,
                processing: {
                    supplyType: 'barre',
                    currentBar: {
                        barLength: config.barLength,
                        quantity: config.quantity
                    },
                    barGroups: groups
                }
            }
        });

        // Move directly to drainage process
        setStep(4);
    };


    // Step type selection now handled by DrainageProcess component

    // Handle drainage choice for "Fornitura a Misura"
    const handleMisuraDrainageChoice = (needsDrainage) => {
        if (needsDrainage) {
            // Switch to progettazione
            updateProcessingData({ supplyType: 'progettazione', needsDrainage: true });
            setStep(9);
        } else {
            // Complete without drainage
            completeProcessing();
        }
    };


    // Notes are now handled directly via onChange in the textarea

    // Complete the processing and add to cart
    const completeProcessing = async () => {
        try {
            // For progettazione orders, upload files first
            let uploadedFiles = [];
            if (processingData.supplyType === 'progettazione' && processingData.photos && processingData.photos.length > 0) {
                console.log('Uploading progettazione files...');
                const uploadResult = await api.uploadProjectFiles(processingData.photos);

                if (uploadResult.status === 'success') {
                    uploadedFiles = uploadResult.uploaded_files;
                    console.log('Files uploaded successfully:', uploadedFiles);
                } else {
                    console.error('File upload failed:', uploadResult.message);
                    alert('Errore nel caricamento dei file. Riprova.');
                    return;
                }
            }

            // Calculate quantity in METERS based on supply type
            let finalQuantity = processingData.quantity;

            if (processingData.supplyType === 'barre') {
                finalQuantity = processingData.barGroups
                    ? processingData.barGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0)
                    : processingData.quantity * processingData.barLength;
            } else if (processingData.supplyType === 'misura' && processingData.customBars && processingData.customBars.length > 0) {
                // For misura: calculate total millimeters from custom bars, then convert to meters with 3 decimals
                const totalMm = processingData.customBars.reduce((sum, bar) => sum + ((bar.quantity || 0) * (bar.length || 0)), 0);
                finalQuantity = parseFloat((totalMm / 1000).toFixed(3));
            }
            // For progettazione, keep quantity as is (or set to 0 if undefined)

            // Update the global order context with processing data
            dispatch({
                type: 'UPDATE_PROCESSING',
                payload: {
                    supplyType: processingData.supplyType,
                    barLength: processingData.barLength,
                    barGroups: processingData.barGroups,
                    quantity: finalQuantity, // Use calculated quantity in meters
                    customBars: processingData.customBars,
                    needsDrainage: processingData.needsDrainage,
                    stepType: processingData.stepType,
                    customHoles: processingData.customHoles,
                    photos: processingData.photos,
                    uploadedFiles: uploadedFiles, // Store uploaded file metadata
                    notes: processingData.notes,
                    metriIndicativi: processingData.metriIndicativi,
                    vuoiGiunzioni: processingData.vuoiGiunzioni,
                    giunzioniDettaglio: processingData.giunzioniDettaglio,
                    vuoiCorrimano: processingData.vuoiCorrimano,
                    corrimanoDettaglio: processingData.corrimanoDettaglio,
                    vuoiTappi: processingData.vuoiTappi,
                    vuoiSpessori: processingData.vuoiSpessori,
                    vuoiCoprifilo: processingData.vuoiCoprifilo,
                    coprifiloDettaglio: processingData.coprifiloDettaglio,
                    vuoiLed: processingData.vuoiLed,
                    // Set isCustomLength = true for "misura" and "progettazione" supply types
                    isCustomLength: processingData.supplyType === 'misura' || processingData.supplyType === 'progettazione',
                    // Clear the length field for custom bars so the T suffix is added correctly
                    length: (processingData.supplyType === 'misura' || processingData.supplyType === 'progettazione') ? null : undefined,
                    action: 'finish'
                }
            });

            // Add the profile article to the cart with processing details
            console.log('ProcessingSection - Adding to cart with data:', {
                supplyType: processingData.supplyType,
                barLength: processingData.barLength,
                customBars: processingData.customBars,
                photos: processingData.photos?.length,
                uploadedFiles: uploadedFiles.length
            });

            dispatch({
                type: 'ADD_TO_CART',
                payload: {
                    // Set quantity at root level for price calculation
                    quantity: finalQuantity,
                    // Include processing details in the cart item
                    processing: {
                        supplyType: processingData.supplyType,
                        barLength: processingData.barLength,
                    barGroups: processingData.barGroups,
                        quantity: finalQuantity, // Use calculated quantity in meters
                        customBars: processingData.customBars,
                        photos: processingData.photos,
                        uploadedFiles: uploadedFiles,
                        notes: processingData.notes,
                        metriIndicativi: processingData.metriIndicativi,
                        vuoiGiunzioni: processingData.vuoiGiunzioni,
                        giunzioniDettaglio: processingData.giunzioniDettaglio,
                        vuoiCorrimano: processingData.vuoiCorrimano,
                        corrimanoDettaglio: processingData.corrimanoDettaglio,
                        vuoiTappi: processingData.vuoiTappi,
                        vuoiSpessori: processingData.vuoiSpessori,
                        vuoiCoprifilo: processingData.vuoiCoprifilo,
                        coprifiloDettaglio: processingData.coprifiloDettaglio,
                        vuoiLed: processingData.vuoiLed,
                        needsDrainage: processingData.needsDrainage
                    },
                    // Ensure wizardSteps is set so the cart display works
                    wizardSteps: {
                        profile: true,
                        carter: true,
                        glass: true,
                        finish: true,
                        fixing: true,
                        length: true,
                        processing: true
                    }
                }
            });

            // Complete the processing section
            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error('Error completing processing:', error);
            alert('Errore durante il completamento della configurazione. Riprova.');
        }
    };

    // Handle drainage process completion
    const handleDrainageComplete = (drainageConfig) => {
        updateProcessingData({
            drainageType: drainageConfig.drainageType,
            needsDrainage: drainageConfig.needsDrainage,
            stepType: drainageConfig.stepType,
            customStepValue: drainageConfig.customStepValue
        });

        // If action is progettazione, switch supply type and go to progettazione flow
        if (drainageConfig.action === 'progettazione') {
            updateProcessingData({
                supplyType: 'progettazione',
                needsDrainage: true
            });
            setStep(9); // Go to progettazione (photo upload)
        } else {
            // After drainage is complete, go to caps configuration (mandatory)
            setStep(12); // Caps configuration
        }
    };

    // Handle caps configuration completion
    const handleCapsComplete = () => {
        const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';
        const rawProfile = profileType.replace(/^N/, ''); // Keeps '6S' as-is for cap lookups
        const normalizedProfile = rawProfile.replace(/^6S$/, '6');

        // Check if profile has spacers (only N6, N106, N116, N176, N186)
        const profilesWithSpacers = ['6', '106', '116', '176', '186'];

        if (profilesWithSpacers.includes(normalizedProfile)) {
            console.log('Caps configured, moving to step 13 (spacers)');
            setStep(13);
        } else {
            // Profiles without spacers (N50, N56) - add only caps and skip to step 11
            console.log('Profile without spacers, adding only caps and moving to step 11');

            // Use carter finish (finishY) for caps on profiles with carter (116, 176, 186), otherwise finishC
            const carterColor = state.orderDetails.finishY || state.orderDetails.finishC || state.orderDetails.finishCode || 'A';
            const capConfig = {
                '50': ['N50-6*DX', 'N50-6*SX'],
                '56': ['N56-6*', 'N56-7*DX', 'N56-7*SX']
            };

            const capCodes = capConfig[normalizedProfile] || [];

            // Add caps to cart
            capCodes.forEach(capCode => {
                const quantity = processingData.capsQuantities[capCode] || 0;
                if (quantity > 0) {
                    const finalCode = capCode.replace('*', carterColor);
                    const capArticle = {
                        productCode: finalCode,
                        description: `Tappo - ${capCode.includes('DX') ? 'Destro' : capCode.includes('SX') ? 'Sinistro' : 'Standard'}`,
                        quantity: quantity,
                        isAccessory: true,
                        accessoryType: 'cap',
                        profileType: profileType,
                        isStandaloneArticle: true
                    };

                    dispatch({
                        type: 'ADD_TO_CART',
                        payload: capArticle
                    });
                }
            });

            completeProcessing(); // Skip spacers, complete order and go to cart
        }
    };

    // Handle spacers configuration completion
    const handleSpacersComplete = async () => {
        console.log('Adding main profile, caps and spacers to cart in correct order...');

        // Get profile type and finish from order state
        const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';
        const rawProfile = profileType.replace(/^N/, ''); // Keeps '6S' as-is for cap lookups
        const normalizedProfile = rawProfile.replace(/^6S$/, '6');
        // Use carter finish (finishY) for caps on profiles with carter (116, 176, 186), otherwise finishC
        const carterColor = state.orderDetails.finishY || state.orderDetails.finishC || state.orderDetails.finishCode || 'A';

        try {
            // === STEP 1: ADD MAIN PROFILE FIRST ===
            // For progettazione orders, upload files first
            let uploadedFiles = [];
            if (processingData.supplyType === 'progettazione' && processingData.photos && processingData.photos.length > 0) {
                console.log('Uploading progettazione files...');
                const uploadResult = await api.uploadProjectFiles(processingData.photos);

                if (uploadResult.status === 'success') {
                    uploadedFiles = uploadResult.uploaded_files;
                    console.log('Files uploaded successfully:', uploadedFiles);
                } else {
                    console.error('File upload failed:', uploadResult.message);
                    alert('Errore nel caricamento dei file. Riprova.');
                    return;
                }
            }

            // Calculate quantity in METERS based on supply type
            let finalQuantity = processingData.quantity;

            if (processingData.supplyType === 'barre') {
                finalQuantity = processingData.quantity * processingData.barLength;
            } else if (processingData.supplyType === 'misura' && processingData.customBars && processingData.customBars.length > 0) {
                const totalMm = processingData.customBars.reduce((sum, bar) => sum + ((bar.quantity || 0) * (bar.length || 0)), 0);
                finalQuantity = parseFloat((totalMm / 1000).toFixed(3));
            }

            // Update the global order context with processing data
            dispatch({
                type: 'UPDATE_PROCESSING',
                payload: {
                    supplyType: processingData.supplyType,
                    barLength: processingData.barLength,
                    barGroups: processingData.barGroups,
                    quantity: finalQuantity,
                    customBars: processingData.customBars,
                    needsDrainage: processingData.needsDrainage,
                    stepType: processingData.stepType,
                    customHoles: processingData.customHoles,
                    photos: processingData.photos,
                    uploadedFiles: uploadedFiles,
                    notes: processingData.notes,
                    metriIndicativi: processingData.metriIndicativi,
                    vuoiGiunzioni: processingData.vuoiGiunzioni,
                    giunzioniDettaglio: processingData.giunzioniDettaglio,
                    vuoiCorrimano: processingData.vuoiCorrimano,
                    corrimanoDettaglio: processingData.corrimanoDettaglio,
                    vuoiTappi: processingData.vuoiTappi,
                    vuoiSpessori: processingData.vuoiSpessori,
                    vuoiCoprifilo: processingData.vuoiCoprifilo,
                    coprifiloDettaglio: processingData.coprifiloDettaglio,
                    vuoiLed: processingData.vuoiLed,
                    isCustomLength: processingData.supplyType === 'misura' || processingData.supplyType === 'progettazione',
                    length: (processingData.supplyType === 'misura' || processingData.supplyType === 'progettazione') ? null : undefined,
                    action: 'finish'
                }
            });

            // Add the main profile article to the cart FIRST
            console.log('ProcessingSection - Adding main profile to cart first');
            dispatch({
                type: 'ADD_TO_CART',
                payload: {
                    // Set quantity at root level for price calculation
                    quantity: finalQuantity,
                    processing: {
                        supplyType: processingData.supplyType,
                        barLength: processingData.barLength,
                    barGroups: processingData.barGroups,
                        quantity: finalQuantity,
                        customBars: processingData.customBars,
                        photos: processingData.photos,
                        uploadedFiles: uploadedFiles,
                        notes: processingData.notes,
                        metriIndicativi: processingData.metriIndicativi,
                        vuoiGiunzioni: processingData.vuoiGiunzioni,
                        giunzioniDettaglio: processingData.giunzioniDettaglio,
                        vuoiCorrimano: processingData.vuoiCorrimano,
                        corrimanoDettaglio: processingData.corrimanoDettaglio,
                        vuoiTappi: processingData.vuoiTappi,
                        vuoiSpessori: processingData.vuoiSpessori,
                        vuoiCoprifilo: processingData.vuoiCoprifilo,
                        coprifiloDettaglio: processingData.coprifiloDettaglio,
                        vuoiLed: processingData.vuoiLed,
                        needsDrainage: processingData.needsDrainage
                    },
                    wizardSteps: {
                        profile: true,
                        carter: true,
                        glass: true,
                        finish: true,
                        fixing: true,
                        length: true,
                        processing: true
                    }
                }
            });

            // === STEP 2: ADD CAPS TO CART ===
            const capConfig = {
                '6': ['N6-6*'],
                '6S': ['N6S-6*'],
                '106': ['N106-6*'],
                '116': ['N116-6*DX', 'N116-6*SX'],
                '56': ['N56-6*', 'N56-7*DX', 'N56-7*SX'],
                '50': ['N50-6*DX', 'N50-6*SX'],
                '176': ['N166-6*'],
                '186': ['N166-6*']
            };

            const capCodes = capConfig[rawProfile] || [];

            // Add each cap with its individual quantity
            capCodes.forEach(capCode => {
                const quantity = processingData.capsQuantities[capCode] || 0;
                if (quantity > 0) {
                    const finalCode = capCode.replace('*', carterColor);
                    const capArticle = {
                        productCode: finalCode,
                        description: `Tappo - ${capCode.includes('DX') ? 'Destro' : capCode.includes('SX') ? 'Sinistro' : 'Standard'}`,
                        quantity: quantity,
                        isAccessory: true,
                        accessoryType: 'cap',
                        profileType: profileType,
                        isStandaloneArticle: true
                    };

                    dispatch({
                        type: 'ADD_TO_CART',
                        payload: capArticle
                    });
                }
            });

            // === STEP 3: ADD SPACERS TO CART ===
            // Calculate total meters based on supply type
            let totalMeters = 0;

            if (processingData.supplyType === 'barre') {
                totalMeters = processingData.barGroups
                    ? processingData.barGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0)
                    : processingData.quantity * processingData.barLength;
            } else if (processingData.supplyType === 'misura' && processingData.customBars) {
                const totalMm = processingData.customBars.reduce((sum, bar) => sum + ((bar.quantity || 0) * (bar.length || 0)), 0);
                totalMeters = totalMm / 1000;
            }

            // Different logic based on profile type
            if (normalizedProfile === '176' || normalizedProfile === '186') {
                // For N176/186: Single product with (METRI / 6) * 30
                const spacersQuantity = Math.ceil((totalMeters / 6) * 30);
                const spacerArticle = {
                    productCode: 'N186-SC-A', // Fixed code with Argento finish
                    description: `Spessore Standard`,
                    quantity: spacersQuantity,
                    isAccessory: true,
                    accessoryType: 'spacer',
                    profileType: profileType,
                    isStandaloneArticle: true
                };

                dispatch({
                    type: 'ADD_TO_CART',
                    payload: spacerArticle
                });
            } else if (normalizedProfile === '6' || normalizedProfile === '106' || normalizedProfile === '116') {
                // For N6, N106, N116: TWO products

                // 1. SC100 spacer: (METRI / 6) * 2
                const sc100Quantity = Math.ceil((totalMeters / 6) * 2);
                let sc100Code = '';

                if (normalizedProfile === '6') {
                    sc100Code = 'N6-SC100-*';
                } else if (normalizedProfile === '106') {
                    sc100Code = 'N106-SC100-*';
                } else if (normalizedProfile === '116') {
                    sc100Code = 'N116-SC100-*';
                }

                if (sc100Code) {
                    const finalSC100Code = sc100Code.replace('*', carterColor);
                    const sc100Article = {
                        productCode: finalSC100Code,
                        description: `Spessore 100mm`,
                        quantity: sc100Quantity,
                        isAccessory: true,
                        accessoryType: 'spacer',
                        profileType: profileType,
                        isStandaloneArticle: true
                    };

                    dispatch({
                        type: 'ADD_TO_CART',
                        payload: sc100Article
                    });
                }

                // 2. Profile-specific spacer with different multiplier
                let profileSpacerCode = '';
                let profileSpacerMultiplier = 0;
                let profileSpacerName = '';

                if (normalizedProfile === '6' || normalizedProfile === '106') {
                    profileSpacerMultiplier = 15;
                    profileSpacerCode = normalizedProfile === '6' ? 'N6-SC300-*' : 'N106-SC300-*';
                    profileSpacerName = 'Spessore 300mm';
                } else if (normalizedProfile === '116') {
                    profileSpacerMultiplier = 10;
                    profileSpacerCode = 'N116-SC400-*';
                    profileSpacerName = 'Spessore 400mm';
                }

                if (profileSpacerCode && profileSpacerMultiplier > 0) {
                    const profileSpacerQuantity = Math.ceil((totalMeters / 6) * profileSpacerMultiplier);
                    const finalProfileSpacerCode = profileSpacerCode.replace('*', carterColor);
                    const profileSpacerArticle = {
                        productCode: finalProfileSpacerCode,
                        description: profileSpacerName,
                        quantity: profileSpacerQuantity,
                        isAccessory: true,
                        accessoryType: 'spacer',
                        profileType: profileType,
                        isStandaloneArticle: true
                    };

                    dispatch({
                        type: 'ADD_TO_CART',
                        payload: profileSpacerArticle
                    });
                }
            }

            console.log('Main profile, caps and spacers added in correct order');

            // === STEP 4: Complete and navigate ===
            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error('Error completing processing with caps and spacers:', error);
            alert('Errore durante il completamento della configurazione. Riprova.');
        }
    };

    // Handle accessory choice
    const handleAccessoryChoice = (wantsAccessories) => {
        if (wantsAccessories) {
            // Navigate to accessories page with the current profile context
            // Complete processing first to save the main product
            completeProcessing();

            // Then navigate to accessories with profile context
            if (onComplete) {
                onComplete({
                    redirectToAccessories: true,
                    profileContext: {
                        category: 'ninfa',
                        subCategory: 'accessori'
                    }
                });
            }
        } else {
            // User doesn't want accessories, just complete the process
            completeProcessing();
        }
    };

    // Render different steps based on current state
    const renderStep = () => {
        switch (step) {
            case 1:
                return <SupplyTypeSelector onSelect={handleSupplyTypeSelect} />;

            case 2:
                return <BarConfigSelector onSelect={handleBarConfigSelect} />;


            case 4:
                // Use DrainageProcess component for drainage selection
                return <DrainageProcess onComplete={handleDrainageComplete} />;
            // Cases 4.5 and 5 removed - now handled by DrainageProcess component

            case 6:
                // Dynamic bars input for "Fornitura a Misura" - add as many rows as needed
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Fornitura a Misura
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Aggiungi le barre di cui hai bisogno con il pulsante "+"
                        </p>
                        <div className="space-y-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="font-medium text-red-900">Esempio</h4>
                                </div>
                                <p className="text-sm text-red-800">
                                    {isMax3m
                                        ? '3 barre da 2900mm, 5 barre da 1500mm, 1 barra da 1000mm'
                                        : '3 barre da 2900mm, 5 barre da 5500mm, 1 barra da 1000mm'
                                    }
                                </p>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center mb-2">
                                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <h4 className="font-medium text-yellow-900">Restrizioni</h4>
                                </div>
                                <p className="text-sm text-yellow-800">
                                    {isMax3m
                                        ? 'Lunghezza minima 200mm. Per questo profilo la lunghezza massima è 2999mm (max 3 metri).'
                                        : 'Lunghezza minima 200mm. Non puoi inserire esattamente 3000mm, 6000mm o valori maggiori di 6000mm.'
                                    }
                                </p>
                            </div>

                            {/* Bar rows */}
                            <div className="space-y-3">
                                {processingData.customBars.map((bar, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                max="100"
                                                value={bar.quantity === 0 ? '' : (bar.quantity || '')}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const newBars = [...processingData.customBars];
                                                    newBars[index].quantity = raw === '' ? 0 : (parseInt(raw) || 0);
                                                    updateProcessingData({ customBars: newBars });
                                                }}
                                                className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                                placeholder="N° barre"
                                            />
                                        </div>
                                        <span className="text-gray-500 font-medium">barre da</span>
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                min="1"
                                                max={isMax3m ? 2999 : 5999}
                                                value={bar.length === 0 ? '' : bar.length}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const newBars = [...processingData.customBars];

                                                    // Allow clearing the field
                                                    if (raw === '') {
                                                        newBars[index].length = 0;
                                                        updateProcessingData({ customBars: newBars });
                                                        return;
                                                    }

                                                    const lengthValue = parseInt(raw) || 0;

                                                    // Block invalid lengths based on profile
                                                    // For profiles limited to 3m: block values >= 3000
                                                    // For others: block exactly 3000, 6000, or > 6000
                                                    const isBlocked = isMax3m
                                                        ? lengthValue >= 3000
                                                        : (lengthValue === 3000 || lengthValue === 6000 || lengthValue > 6000);

                                                    if (isBlocked) {
                                                        // Show visual feedback by adding error class
                                                        e.target.classList.add('border-red-500', 'bg-red-50');
                                                        setTimeout(() => {
                                                            e.target.classList.remove('border-red-500', 'bg-red-50');
                                                        }, 1000);
                                                        return;
                                                    }

                                                    newBars[index].length = lengthValue;
                                                    updateProcessingData({ customBars: newBars });
                                                }}
                                                className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                                                placeholder="Millimetri (min 200)"
                                            />
                                        </div>
                                        <span className="text-gray-500 font-medium">mm</span>
                                        <button
                                            onClick={() => {
                                                const newBars = processingData.customBars.filter((_, i) => i !== index);
                                                updateProcessingData({ customBars: newBars });
                                            }}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Rimuovi"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add bar button */}
                            <button
                                onClick={() => {
                                    const newBars = [...processingData.customBars, { quantity: 1, length: 0, index: processingData.customBars.length + 1 }];
                                    updateProcessingData({ customBars: newBars });
                                }}
                                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-400 hover:text-red-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Aggiungi Barra
                            </button>

                            {/* Summary */}
                            {processingData.customBars.length > 0 && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Riepilogo:</h4>
                                    <div className="space-y-1 text-sm text-gray-700">
                                        {processingData.customBars.map((bar, idx) => (
                                            <div key={idx}>
                                                • {bar.quantity || 0} {bar.quantity === 1 ? 'barra' : 'barre'} da {bar.length || 0}mm
                                            </div>
                                        ))}
                                        <div className="pt-2 mt-2 border-t border-gray-300 font-medium">
                                            Totale: {processingData.customBars.reduce((sum, bar) => sum + (bar.quantity || 0), 0)} barre
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Indietro
                                </button>
                                <button
                                    onClick={() => {
                                        // Filter out empty/incomplete rows first (bars with no length or quantity)
                                        const completeBars = processingData.customBars.filter(bar => bar.quantity > 0 && bar.length > 0);

                                        if (completeBars.length === 0) {
                                            alert('Aggiungi almeno una barra con quantità e lunghezza valide');
                                            return;
                                        }

                                        // Validate minimum 200mm
                                        const tooShortBars = completeBars.filter(bar => bar.length < 200);
                                        if (tooShortBars.length > 0) {
                                            alert('Errore: La lunghezza minima per ogni barra è 200mm');
                                            return;
                                        }

                                        // Validate max length based on profile
                                        const invalidBars = isMax3m
                                            ? completeBars.filter(bar => bar.length >= 3000)
                                            : completeBars.filter(bar => bar.length === 3000 || bar.length === 6000 || bar.length > 6000);

                                        if (invalidBars.length > 0) {
                                            alert(isMax3m
                                                ? 'Errore: Per questo profilo la lunghezza massima è 2999mm (max 3 metri)'
                                                : 'Errore: Non puoi inserire barre di esattamente 3000mm, 6000mm o maggiori di 6000mm');
                                            return;
                                        }

                                        // All validations passed, update with only complete bars and proceed
                                        updateProcessingData({ customBars: completeBars });
                                        setStep(8); // Go to drainage question
                                    }}
                                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                                    disabled={processingData.customBars.length === 0}
                                >
                                    Avanti
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 8:
                // Drainage question for "Fornitura a Misura"
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-center mb-6">
                            Vuoi aggiungere foro o fresatura scarico acqua?
                        </h3>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-yellow-800">
                                <strong>Nota:</strong> Per la fornitura a misura con scarico acqua è necessario passare per la progettazione.
                            </p>
                        </div>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={() => handleMisuraDrainageChoice(true)}
                                className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Sì, vai a Progettazione
                            </button>
                            <button
                                onClick={() => handleMisuraDrainageChoice(false)}
                                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                            >
                                No, completa ordine
                            </button>
                        </div>
                    </div>
                );

            case 9:
                // Photo upload with dropzone
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Fornitura con Progettazione
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Aggiungi file del progetto per una valutazione accurata
                        </p>

                        <div className="space-y-6">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h4 className="font-medium text-green-900">Formati supportati</h4>
                                </div>
                                <p className="text-sm text-green-800">
                                    JPG, JPEG, PNG, DWG, PDF, DXF, STL, OBJ - Massimo 10 file per progetto
                                </p>
                            </div>

                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors">
                                <input
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.dwg,.pdf,.dxf,.stl,.obj"
                                    className="hidden"
                                    id="photo-upload"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            const newFiles = Array.from(e.target.files);
                                            const totalFiles = processingData.photos.length + newFiles.length;

                                            if (totalFiles > 10) {
                                                alert('Massimo 10 file consentiti. Seleziona meno file.');
                                                return;
                                            }

                                            const updatedPhotos = [...processingData.photos, ...newFiles];
                                            updateProcessingData({ photos: updatedPhotos });
                                        }
                                    }}
                                />
                                <label htmlFor="photo-upload" className="cursor-pointer">
                                    <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p className="text-lg font-medium text-gray-900 mb-2">
                                        Aggiungi File
                                    </p>
                                    <p className="text-sm text-gray-600 mb-2">
                                        Clicca per selezionare o trascina i file qui
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Puoi scattare foto da mobile o caricare file esistenti
                                    </p>
                                </label>
                            </div>

                            {processingData.photos.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-medium text-gray-900">
                                            File caricati ({processingData.photos.length}/10)
                                        </h4>
                                        <button
                                            onClick={() => updateProcessingData({ photos: [] })}
                                            className="text-sm text-red-600 hover:text-red-800"
                                        >
                                            Rimuovi tutti
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {processingData.photos.map((file, index) => (
                                            <div key={index} className="relative group">
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={`Preview ${index + 1}`}
                                                    className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg" />
                                                <button
                                                    onClick={() => {
                                                        const newPhotos = processingData.photos.filter((_, i) => i !== index);
                                                        updateProcessingData({ photos: newPhotos });
                                                    }}
                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    ×
                                                </button>
                                                <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                                                    {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Indietro
                                </button>
                                <button
                                    onClick={() => setStep(10)}
                                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={processingData.photos.length === 0}
                                >
                                    Avanti
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 10:
                // Notes input
                return (
                    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Note Aggiuntive
                        </h2>
                        <p className="text-gray-600 text-center mb-6 text-sm">
                            Aggiungi dettagli specifici per il tuo progetto personalizzato
                        </p>

                        <div className="space-y-5">
                            {/* Metri indicativi */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Metri indicativi
                                </label>
                                <input
                                    type="text"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Es. 12"
                                    value={processingData.metriIndicativi || ''}
                                    onChange={(e) => updateProcessingData({ metriIndicativi: e.target.value })}
                                />
                            </div>

                            {/* Checkboxes */}
                            <div className="space-y-3">
                                {[
                                    { key: 'vuoiGiunzioni', label: 'Vuoi giunzioni?', detailKey: 'giunzioniDettaglio', needsDetail: true },
                                    { key: 'vuoiCorrimano', label: 'Vuoi corrimano?', detailKey: 'corrimanoDettaglio', needsDetail: true },
                                    { key: 'vuoiTappi', label: 'Vuoi tappi?', needsDetail: false },
                                    { key: 'vuoiSpessori', label: 'Vuoi spessori?', needsDetail: false },
                                    { key: 'vuoiCoprifilo', label: 'Vuoi coprifilo?', detailKey: 'coprifiloDettaglio', needsDetail: true },
                                    { key: 'vuoiLed', label: 'Vuoi led?', needsDetail: false },
                                ].map(({ key, label, detailKey, needsDetail }) => (
                                    <div key={key}>
                                        <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                            <input
                                                type="checkbox"
                                                className="w-5 h-5 text-red-500 rounded border-gray-300 focus:ring-red-500"
                                                checked={processingData[key] || false}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    const updates = { [key]: checked };
                                                    if (!checked && detailKey) {
                                                        updates[detailKey] = '';
                                                    }
                                                    if (checked && needsDetail) {
                                                        setDetailModal({ open: true, field: detailKey, label });
                                                    }
                                                    updateProcessingData(updates);
                                                }}
                                            />
                                            <span className="text-sm font-medium text-gray-700">{label}</span>
                                            {processingData[key] && detailKey && processingData[detailKey] && (
                                                <span className="ml-auto text-xs text-gray-500 truncate max-w-[150px] sm:max-w-[250px]">
                                                    {processingData[detailKey]}
                                                </span>
                                            )}
                                            {processingData[key] && needsDetail && (
                                                <button
                                                    type="button"
                                                    className="ml-auto text-xs text-red-500 underline"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setDetailModal({ open: true, field: detailKey, label });
                                                    }}
                                                >
                                                    {processingData[detailKey] ? 'Modifica' : 'Specifica'}
                                                </button>
                                            )}
                                        </label>
                                    </div>
                                ))}
                            </div>

                            {/* Free text notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descrizione del progetto e specifiche tecniche
                                </label>
                                <textarea
                                    id="project-description"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                    rows="4"
                                    placeholder="Descrivi il progetto, specifiche tecniche, vincoli, tempistiche..."
                                    value={processingData.notes || ''}
                                    onChange={(e) => updateProcessingData({ notes: e.target.value })}
                                />
                            </div>

                            {/* Riepilogo */}
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <h4 className="font-medium text-red-900 mb-2">Riepilogo progetto</h4>
                                <div className="text-sm text-red-800 space-y-1">
                                    <div className="flex justify-between">
                                        <span>Tipo fornitura:</span>
                                        <span className="font-medium">Con Progettazione</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>File caricati:</span>
                                        <span className="font-medium">{processingData.photos.length} foto</span>
                                    </div>
                                    {processingData.metriIndicativi && (
                                        <div className="flex justify-between">
                                            <span>Metri indicativi:</span>
                                            <span className="font-medium">{processingData.metriIndicativi}</span>
                                        </div>
                                    )}
                                    {processingData.customBars && processingData.customBars.length > 0 && (
                                        <div className="flex justify-between">
                                            <span>Barre configurate:</span>
                                            <span className="font-medium">{processingData.customBars.length}</span>
                                        </div>
                                    )}
                                    {processingData.notes && processingData.notes.trim() && (
                                        <div className="flex justify-between">
                                            <span>Descrizione progetto:</span>
                                            <span className="font-medium">Inclusa</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between">
                                <button
                                    onClick={() => setStep(9)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Indietro
                                </button>
                                <button
                                    onClick={() => {
                                        completeProcessing();
                                    }}
                                    className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                >
                                    Completa Progetto
                                </button>
                            </div>
                        </div>

                        {/* Detail Modal for checkbox specifications */}
                        {detailModal.open && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                                <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                                    <h3 className="text-lg font-semibold mb-3">{detailModal.label}</h3>
                                    <p className="text-sm text-gray-600 mb-4">Quale/i? Specifica i dettagli:</p>
                                    <textarea
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                        rows="4"
                                        placeholder="Scrivi qui i dettagli..."
                                        value={processingData[detailModal.field] || ''}
                                        onChange={(e) => updateProcessingData({ [detailModal.field]: e.target.value })}
                                        autoFocus
                                    />
                                    <div className="flex justify-end gap-3 mt-4">
                                        <button
                                            onClick={() => setDetailModal({ open: false, field: '', label: '' })}
                                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                                        >
                                            Annulla
                                        </button>
                                        <button
                                            onClick={() => setDetailModal({ open: false, field: '', label: '' })}
                                            className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                            Conferma
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 12:
                // Caps configuration (mandatory)
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Configurazione Tappi Laterali
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Inserisci la quantità per ogni tipo di tappo
                        </p>

                        {(() => {
                            const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';
                            const rawProfile = profileType.replace(/^N/, '');
                            const normalizedProfile = rawProfile.replace(/^6S$/, '6');
                            // Use carter color (finishC) for caps, fallback to finishCode for backwards compatibility
                            const carterColor = state.orderDetails.finishC || state.orderDetails.finishCode || 'A';
                            const finishName = {
                                'A': 'Argento',
                                'B': 'RAL9010 Opaco',
                                'N': 'RAL9005 Opaco',
                                'G': 'Grezzi',
                                'V1': 'Verniciato Speciale 1',
                                'V2': 'Verniciato Speciale 2'
                            }[carterColor] || carterColor;

                            // Define cap configurations for each profile
                            const capConfig = {
                                '6': ['N6-6*'],
                                '6S': ['N6S-6*'],
                                '106': ['N106-6*'],
                                '116': ['N116-6*DX', 'N116-6*SX'],
                                '56': ['N56-6*', 'N56-7*DX', 'N56-7*SX'],
                                '50': ['N50-6*DX', 'N50-6*SX'],
                                '176': ['N166-6*'],
                                '186': ['N166-6*']
                            };

                            const capCodes = capConfig[rawProfile] || [];

                            // Initialize quantities if not set
                            if (Object.keys(processingData.capsQuantities).length === 0 && capCodes.length > 0) {
                                const initialQuantities = {};
                                capCodes.forEach(code => {
                                    initialQuantities[code] = 0; // Default 0 - user can choose quantity
                                });
                                updateProcessingData({ capsQuantities: initialQuantities });
                            }

                            const updateCapQuantity = (capCode, quantity) => {
                                const newQuantities = {
                                    ...processingData.capsQuantities,
                                    [capCode]: quantity === '' ? '' : Math.max(0, parseInt(quantity) || 0)
                                };
                                updateProcessingData({ capsQuantities: newQuantities });
                            };

                            return (
                                <div className="space-y-6">
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <p className="text-sm text-red-900">
                                            <strong>Profilo:</strong> NINFA {rawProfile === '6S' ? '6 STADIO' : normalizedProfile}
                                        </p>
                                        <p className="text-sm text-red-900">
                                            <strong>Colore automatico:</strong> {finishName}
                                        </p>
                                    </div>

                                    {/* Individual cap type quantity inputs */}
                                    <div className="space-y-4">
                                        {capCodes.map(capCode => {
                                            const capName = capCode.includes('DX') ? 'Tappo Destro' :
                                                capCode.includes('SX') ? 'Tappo Sinistro' : capCode.includes('N56-7') && capCode.includes('SX') ? 'Tappo Antiscalata Sinistro' : capCode.includes('N56-7') && capCode.includes('DX') ? 'Tappo Antiscalata Sinistro' :
                                                    capCode.includes('7') ? 'Tappo Tipo 7' : 'Tappo Standard';
                                            const displayCode = capCode.replace('*', carterColor);

                                            return (
                                                <div key={capCode} className="border border-gray-200 rounded-lg p-4">
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                        <div className="flex-1">
                                                            <h3 className="font-medium text-gray-900">{capName}</h3>
                                                            <p className="text-sm text-gray-500">Codice: {displayCode}</p>
                                                        </div>
                                                        <div className="flex items-center space-x-3 w-full sm:w-auto">
                                                            <label className="text-sm font-medium text-gray-700">Quantità:</label>
                                                            <input
                                                                type="number"
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                value={processingData.capsQuantities[capCode] ?? 0}
                                                                onChange={(e) => updateCapQuantity(capCode, e.target.value)}
                                                                onBlur={(e) => { if (e.target.value === '') updateCapQuantity(capCode, '0'); }}
                                                                className="flex-1 sm:flex-none sm:w-32 p-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-center"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Summary */}
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-2">Riepilogo Tappi:</h4>
                                        <div className="text-sm text-gray-700">
                                            {capCodes.map(capCode => {
                                                const qty = processingData.capsQuantities[capCode] || 0;
                                                if (qty === 0) return null;
                                                const displayCode = capCode.replace('*', carterColor);
                                                const capName = capCode.includes('DX') ? 'Destro' :
                                                    capCode.includes('SX') ? 'Sinistro' :
                                                        capCode.includes('7') ? 'Tipo 7' : 'Standard';
                                                return (
                                                    <div key={capCode} className="py-1">
                                                        • {displayCode} - {capName}: {qty} pz
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="flex justify-center space-x-4 mt-6">
                                        <button
                                            onClick={() => {
                                                // Set all cap quantities to 0
                                                const resetQuantities = {};
                                                capCodes.forEach(code => {
                                                    resetQuantities[code] = 0;
                                                });
                                                updateProcessingData({ capsQuantities: resetQuantities });

                                                // Proceed to next step
                                                handleCapsComplete();
                                            }}
                                            className="px-6 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                                        >
                                            Non voglio tappi
                                        </button>
                                        <button
                                            onClick={handleCapsComplete}
                                            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                        >
                                            Continua
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                );

            case 13:
                // Spacers configuration (mandatory)
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Configurazione Spessori
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            La quantità viene calcolata automaticamente in base ai metri totali e al profilo
                        </p>

                        {(() => {
                            const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';
                            const rawProfile = profileType.replace(/^N/, '');
                            const normalizedProfile = rawProfile.replace(/^6S$/, '6');
                            // Use carter color (finishC) for spacers, fallback to finishCode for backwards compatibility
                            const carterColor = state.orderDetails.finishC || state.orderDetails.finishCode || 'A';
                            const finishName = {
                                'A': 'Argento',
                                'B': 'RAL9010 Opaco',
                                'N': 'RAL9005 Opaco',
                                'G': 'Grezzi',
                                'V1': 'Verniciato Speciale 1',
                                'V2': 'Verniciato Speciale 2'
                            }[carterColor] || carterColor;

                            // Calculate total meters
                            let totalMeters = 0;
                            if (processingData.supplyType === 'barre') {
                                totalMeters = processingData.barGroups
                                    ? processingData.barGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0)
                                    : processingData.quantity * processingData.barLength;
                            } else if (processingData.supplyType === 'misura' && processingData.customBars) {
                                const totalMm = processingData.customBars.reduce((sum, bar) => sum + ((bar.quantity || 0) * (bar.length || 0)), 0);
                                totalMeters = totalMm / 1000;
                            }

                            // Calculate spacers based on profile type
                            let spacerProducts = [];

                            if (normalizedProfile === '176' || normalizedProfile === '186') {
                                // N176/186: Single product with (METRI / 6) * 30
                                const quantity = Math.ceil((totalMeters / 6) * 30);
                                spacerProducts.push({
                                    code: 'N186-SC-A',
                                    name: 'Spessore Standard',
                                    quantity: quantity,
                                    formula: `(${totalMeters.toFixed(2)} / 6) × 30 = ${quantity}`
                                });
                            } else if (normalizedProfile === '6' || normalizedProfile === '106' || normalizedProfile === '116') {
                                // For N6, N106, N116: TWO products

                                // 1. SC100 spacer
                                const sc100Quantity = Math.ceil((totalMeters / 6) * 2);
                                let sc100Code = '';
                                if (normalizedProfile === '6') sc100Code = 'N6-SC100-' + carterColor;
                                else if (normalizedProfile === '106') sc100Code = 'N106-SC100-' + carterColor;
                                else if (normalizedProfile === '116') sc100Code = 'N116-SC100-' + carterColor;

                                if (sc100Code) {
                                    spacerProducts.push({
                                        code: sc100Code,
                                        name: 'Spessore 100mm',
                                        quantity: sc100Quantity,
                                        formula: `(${totalMeters.toFixed(2)} / 6) × 2 = ${sc100Quantity}`
                                    });
                                }

                                // 2. Profile-specific spacer
                                let profileSpacerCode = '';
                                let profileSpacerName = '';
                                let profileSpacerMultiplier = 0;

                                if (normalizedProfile === '6' || normalizedProfile === '106') {
                                    profileSpacerMultiplier = 14;
                                    profileSpacerCode = normalizedProfile === '6' ? 'N6-SC300-' + carterColor : 'N106-SC300-' + carterColor;
                                    profileSpacerName = 'Spessore 300mm';
                                } else if (normalizedProfile === '116') {
                                    profileSpacerMultiplier = 10;
                                    profileSpacerCode = 'N116-SC400-' + carterColor;
                                    profileSpacerName = 'Spessore 400mm';
                                }

                                if (profileSpacerCode && profileSpacerMultiplier > 0) {
                                    const profileSpacerQuantity = Math.ceil((totalMeters / 6) * profileSpacerMultiplier);
                                    spacerProducts.push({
                                        code: profileSpacerCode,
                                        name: profileSpacerName,
                                        quantity: profileSpacerQuantity,
                                        formula: `(${totalMeters.toFixed(2)} / 6) × ${profileSpacerMultiplier} = ${profileSpacerQuantity}`
                                    });
                                }
                            }

                            return (
                                <div className="space-y-6">
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <p className="text-sm text-red-900">
                                            <strong>Profilo:</strong> NINFA {rawProfile === '6S' ? '6 STADIO' : normalizedProfile}
                                        </p>
                                        <p className="text-sm text-red-900">
                                            <strong>Colore automatico:</strong> {finishName}
                                        </p>
                                        <p className="text-sm text-red-900 mt-2">
                                            <strong>Metri totali:</strong> {totalMeters.toFixed(2)} m
                                        </p>
                                    </div>

                                    {/* Display calculated spacer products */}
                                    <div className="space-y-3">
                                        {spacerProducts.map((product, index) => (
                                            <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4">
                                                <h4 className="font-medium text-green-900 mb-1">{product.name}</h4>
                                                <p className="text-sm text-green-800">Codice: {product.code}</p>
                                                <p className="text-sm text-green-800">Formula: {product.formula}</p>
                                                <p className="text-lg font-semibold text-green-900 mt-2">
                                                    Quantità: {product.quantity} pz
                                                </p>
                                            </div>
                                        ))}
                                    </div>

                                    {normalizedProfile !== '176' && normalizedProfile !== '186' && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-sm text-yellow-800">
                                                <strong>Nota:</strong> Per questo profilo vengono aggiunti due tipi di spessori con formule diverse.
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex justify-center space-x-4 mt-6">
                                        <button
                                            onClick={() => setStep(12)}
                                            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                        >
                                            Indietro
                                        </button>
                                        <button
                                            onClick={() => {
                                                // Skip spacers - add only caps and go to step 11
                                                console.log('Skipping spacers, adding only caps');

                                                const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';
                                                const rawProfile = profileType.replace(/^N/, '');
                                                // Use carter color (finishC) for caps
                                                const carterColorForCaps = state.orderDetails.finishC || state.orderDetails.finishCode || 'A';

                                                const capConfig = {
                                                    '6': ['N6-6*'],
                                                    '6S': ['N6S-6*'],
                                                    '106': ['N106-6*'],
                                                    '116': ['N116-6*DX', 'N116-6*SX'],
                                                    '176': ['N166-6*'],
                                                    '186': ['N166-6*']
                                                };

                                                const capCodes = capConfig[rawProfile] || [];

                                                // Add only caps to cart
                                                capCodes.forEach(capCode => {
                                                    const quantity = processingData.capsQuantities[capCode] || 0;
                                                    if (quantity > 0) {
                                                        const finalCode = capCode.replace('*', carterColorForCaps);
                                                        const capArticle = {
                                                            productCode: finalCode,
                                                            description: `Tappo - ${capCode.includes('DX') ? 'Destro' : capCode.includes('SX') ? 'Sinistro' : 'Standard'}`,
                                                            quantity: quantity,
                                                            isAccessory: true,
                                                            accessoryType: 'cap',
                                                            profileType: profileType,
                                                            isStandaloneArticle: true
                                                        };

                                                        dispatch({
                                                            type: 'ADD_TO_CART',
                                                            payload: capArticle
                                                        });
                                                    }
                                                });

                                                completeProcessing();
                                            }}
                                            className="px-6 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                                        >
                                            Non voglio spessori
                                        </button>
                                        <button
                                            onClick={handleSpacersComplete}
                                            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                        >
                                            Conferma e Continua
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                );

            case 11:
                // Accessory confirmation step
                return (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-center mb-2">
                            Configurazione Completata!
                        </h2>
                        <p className="text-gray-600 text-center mb-6">
                            Il tuo profilo è stato configurato con successo. Vuoi aggiungere degli accessori compatibili?
                        </p>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center mb-3">
                                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h4 className="font-medium text-red-900">Accessori disponibili</h4>
                            </div>
                            <p className="text-sm text-red-800">
                                Puoi configurare accessori compatibili con il tuo profilo come corrimani, staffe, coprifili e altro ancora.
                            </p>
                        </div>

                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={() => handleAccessoryChoice(true)}
                                className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                                Sì, configura accessori
                            </button>
                            <button
                                onClick={() => handleAccessoryChoice(false)}
                                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                No, vai al carrello
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-4">
            {renderStep()}

            {/* Progress Indicator */}
            <div className="mt-8 flex justify-center space-x-2">
                {[1, 2, 4, 6, 8, 9, 10, 11, 12, 13].map((stepNumber) => {
                    // Determine which steps are relevant for current flow
                    let isRelevant = false;
                    if (processingData.supplyType === 'barre') {
                        isRelevant = stepNumber <= 4 || stepNumber === 11 || stepNumber === 12 || stepNumber === 13;
                    } else if (processingData.supplyType === 'misura') {
                        isRelevant = (stepNumber === 6 || stepNumber === 8) || stepNumber === 11 || stepNumber === 12 || stepNumber === 13;
                    } else if (processingData.supplyType === 'progettazione') {
                        isRelevant = stepNumber >= 9;
                    } else {
                        isRelevant = stepNumber === 1;
                    }

                    if (!isRelevant) return null;

                    return (
                        <div
                            key={stepNumber}
                            className={`w-2 h-2 rounded-full ${stepNumber === Math.floor(step)
                                ? 'bg-red-500'
                                : stepNumber < step
                                    ? 'bg-red-200'
                                    : 'bg-gray-200'
                                }`}
                        />
                    );
                })}
            </div>
        </div>
    );
});

export default ProcessingSection;